<script lang="ts" setup>
	import { Download, History, Search } from "lucide-vue-next";
	import { FilterState } from "~/utils/enums";
	import { limitSearchEntries } from "~/utils/searchEntryLimit";
	import { textValidator } from "~/utils/validators";
	import { DropDownOption } from "@/utils/types";

type SearchSubmitPayload = {
	value: string;
	values: string[];
};

type HistoryDropDownOption = {
	value: string;
	label: string;
	description?: string;
	title?: string;
};

type SuggestionDropDownOption = {
	value: string;
	label: string;
	description?: string;
	title?: string;
	state?: string | null;
};

// The two panels shown while typing: completions of the current entry, or the
// person's recent searches. The column selector ("full") is a separate view.
type DropdownPanelView = "suggestions" | "history";

const props = withDefaults(
	defineProps<{
		value?: string;
		values?: string[];
		placeholder?: string;
		filterStates?: FilterState[];
		dropdownOptions: DropDownOption[];
		secondaryDropdownOptions?: DropDownOption[];
		secondaryDropdownLabel?: string;
		secondaryDropdownDownloadLabel?: string;
		secondaryDropdownDeleteLabel?: string;
		historyOptions?: HistoryDropDownOption[];
		historyDropdownLabel?: string;
		historyDeleteLabel?: string;
		historyClearLabel?: string;
		historyLoading?: boolean;
		historyActionLabel?: string;
		historyEmptyLabel?: string;
		suggestionOptions?: SuggestionDropDownOption[];
		suggestionsEnabled?: boolean;
		suggestionsLoading?: boolean;
		suggestionDropdownLabel?: string;
		suggestionEmptyLabel?: string;
		openOnFocus?: boolean;
		dropdownActionLabel?: string;
		valuesLabel?: string;
		disabled?: boolean;
		showDropdown?: boolean;
		singleSelectDropdown?: boolean;
		allowMultiple?: boolean;
		maxValues?: number;
		splitOrValues?: boolean;
		showSubmitButton?: boolean;
		submitButtonLabel?: string;
		searchModes?: { value: string; label: string; description?: string }[];
		activeSearchMode?: string;
	}>(),
	{
		value: "",
		values: () => [],
		placeholder: "",
		secondaryDropdownOptions: () => [],
		secondaryDropdownLabel: "",
		secondaryDropdownDownloadLabel: "",
		secondaryDropdownDeleteLabel: "",
		historyOptions: () => [],
		historyDropdownLabel: "",
		historyDeleteLabel: "",
		historyClearLabel: "",
		historyLoading: false,
		historyActionLabel: "",
		historyEmptyLabel: "No recent searches",
		suggestionOptions: () => [],
		suggestionsEnabled: false,
		suggestionsLoading: false,
		suggestionDropdownLabel: "Suggestions",
		suggestionEmptyLabel: "No matching names",
		openOnFocus: false,
		dropdownActionLabel: "",
		valuesLabel: "",
		disabled: false,
		showDropdown: true,
		singleSelectDropdown: false,
		allowMultiple: false,
		splitOrValues: false,
		showSubmitButton: false,
		submitButtonLabel: "Search",
		searchModes: () => [],
		activeSearchMode: "",
	}
);

	const emit = defineEmits<{
		(e: "update:value", value: string): void;
		(e: "update:values", value: string[]): void;
		(e: "mode:selected", value: string): void;
		(e: "change", value: string): void;
		(e: "focusin"): void;
		(e: "focusout"): void;
		(e: "enterpressed", payload: SearchSubmitPayload): void;
		(e: "values-limit-reached", payload: { attemptedCount: number; maxValues: number }): void;
		(e: "dropdown:selected", selectedValue: string): void;
		(e: "dropdown:secondary-selected", selectedValue: string): void;
		(e: "dropdown:secondary-download", selectedValue: string): void;
		(e: "dropdown:secondary-delete", selectedValue: string): void;
		(e: "dropdown:history-selected", selectedValue: string): void;
		(e: "dropdown:history-delete", selectedValue: string): void;
		(e: "dropdown:history-clear"): void;
		(e: "dropdown:history-action"): void;
		(e: "dropdown:suggestion-selected", selectedValue: string, selectedState?: string | null): void;
		(e: "dropdown:action"): void;
		(e: "values-label:cleared"): void;
		(e: "suggesion:selected", value: string): void;
		(e: "updateClickedValue", value: string[]): void;
	}>();

	const hasFocus = ref<boolean>(false);
	const DropDownActive = ref<boolean>(false);
	// "full" = column selector + saved lists (icon click); "history" = recent searches only (input focus).
	const dropdownView = ref<"full" | "history" | "suggestions">("full");
	const modeMenuOpen = ref<boolean>(false);
	const modeMenuRef = ref<HTMLElement | null>(null);
	const rootRef = ref<HTMLElement | null>(null);
	const dropDownRef = ref<HTMLElement | null>(null);
	const editingValueElement = ref<HTMLElement | null>(null);
	const editingValue = ref<string>("");
	const editingTarget = ref<string | null>(null);
	const selectedDropdownValues = ref<string[]>([]);
	// const ShowSuggestion = ref<boolean>(false);

	// const suggestionsList = computed(() => {
	//     return props.suggestions
	//         .find(x => x.id == 'industry')
	//         ?.settings
	//         .options
	//         ?.map(x => x.value)
	//         .filter(x => x.toLowerCase().startsWith(value.value.toLowerCase()))
	//         ?? [];
	// });

	const value = computed({
		get() {
			return props.value;
		},
		set(value: string) {
			// ShowSuggestion.value = value.length > 0 && selectedOptions.value == 'industry' && !DropDownActive.value

			emit("update:value", value);
		},
	});

	const values = computed({
		get() {
			return props.values ?? [];
		},
		set(values: string[]) {
			emit("update:values", values);
		},
	});

	const selectedDropdownOption = computed(() => {
		if (!props.dropdownOptions?.length) return null;
		const selectedValue = selectedDropdownValues.value[0];
		if (selectedValue) {
			return props.dropdownOptions.find((option) => option.value === selectedValue) ?? null;
		}
		return props.dropdownOptions.find((option) => option.selected) ?? null;
	});

	const selectedDropdownLabel = computed(() => {
		return selectedDropdownOption.value?.label ?? "";
	});

	watch(
		() => props.dropdownOptions.map((option) => ({ value: option.value, selected: option.selected })),
		(next) => {
			const selectedValues = next.filter((option) => option.selected).map((option) => option.value);
			selectedDropdownValues.value = props.singleSelectDropdown ? selectedValues.slice(0, 1) : selectedValues;
		},
		{ immediate: true }
	);

	const highlightedSuggestionIndex = ref(-1);
	// The text input reports Enter on keyup; picking a highlighted suggestion on
	// keydown must not also submit the typed prefix a moment later.
	let skipNextEnter = false;

	const hasSuggestionQuery = computed(() => props.suggestionsEnabled && value.value.trim().length > 0);

	// The typing-time panel follows the input: recent searches while it is empty,
	// matching names as soon as there is an entry to complete.
	function autoPanelView(): DropdownPanelView {
		return hasSuggestionQuery.value ? "suggestions" : "history";
	}

	function canOpenTypingPanel() {
		return props.showDropdown && !props.disabled && (props.suggestionsEnabled || props.historyOptions.length > 0 || props.historyLoading || props.historyDropdownLabel);
	}

	function openTypingPanel() {
		if (!canOpenTypingPanel()) return;
		dropdownView.value = props.suggestionsEnabled ? autoPanelView() : "history";
		DropDownActive.value = true;
	}

	function suggestionDropDownSelect(option: SuggestionDropDownOption) {
		DropDownActive.value = false;
		highlightedSuggestionIndex.value = -1;
		emit("dropdown:suggestion-selected", option.value, option.state);
	}

	function handleKeydown(event: KeyboardEvent) {
		if (editingTarget.value) return;
		if (event.key === "Escape" && DropDownActive.value) {
			event.preventDefault();
			DropDownActive.value = false;
			return;
		}
		if (event.key === "ArrowDown" || event.key === "ArrowUp") {
			if (!DropDownActive.value && hasSuggestionQuery.value) openTypingPanel();
			if (!DropDownActive.value || dropdownView.value !== "suggestions") return;
			event.preventDefault();
			const count = props.suggestionOptions.length;
			if (!count) return;
			const step = event.key === "ArrowDown" ? 1 : -1;
			const current = highlightedSuggestionIndex.value;
			highlightedSuggestionIndex.value = current < 0 ? (step > 0 ? 0 : count - 1) : (current + step + count) % count;
			return;
		}
		if (event.key === "Enter" && DropDownActive.value && dropdownView.value === "suggestions") {
			const option = props.suggestionOptions[highlightedSuggestionIndex.value];
			if (!option) return;
			event.preventDefault();
			skipNextEnter = true;
			suggestionDropDownSelect(option);
		}
	}

	function focusIn() {
		hasFocus.value = true;
		emit("focusin");
		if (props.openOnFocus) {
			openTypingPanel();
		}
	}

	function focusOut() {
		hasFocus.value = false;
		emit("focusout");
	}

	// function suggesionClicked(selectedValue: string) {
	// 	const industry = props.suggestions
	// 		.find(x => x.id == 'industry')
	// 	if (industry) {
	// 		industry.open = true;
	// 		industry.values.push(selectedValue)
	// 	}
	// 	value.value = selectedValue
	// 	ShowSuggestion.value = false
	// 	emit('suggesion:selected', selectedValue)
	// }
	//
	function dropDownSelect(option: DropDownOption) {
		if (props.singleSelectDropdown) {
			props.dropdownOptions?.forEach((opt) => (opt.selected = opt.value === option.value));
			selectedDropdownValues.value = [option.value];
			DropDownActive.value = false;
		} else {
			option.selected = !option.selected;
			if (selectedDropdownValues.value) {
				if (selectedDropdownValues.value.includes(option.value)) {
					selectedDropdownValues.value = selectedDropdownValues.value.filter((item) => item !== option.value);
				} else {
					selectedDropdownValues.value = [...selectedDropdownValues.value, option.value];
				}
			} else {
				selectedDropdownValues.value = [option.value];
			}
		}

		emit("dropdown:selected", option.value);

		if (selectedDropdownValues.value) emit("updateClickedValue", selectedDropdownValues.value);
	}

	function secondaryDropDownSelect(option: DropDownOption) {
		DropDownActive.value = false;
		emit("dropdown:secondary-selected", option.value);
	}

	function secondaryDropDownDelete(option: DropDownOption) {
		emit("dropdown:secondary-delete", option.value);
	}

	function secondaryDropDownDownload(option: DropDownOption) {
		emit("dropdown:secondary-download", option.value);
	}

	function historyDropDownSelect(option: HistoryDropDownOption) {
		DropDownActive.value = false;
		emit("dropdown:history-selected", option.value);
	}

	function historyDropDownDelete(option: HistoryDropDownOption) {
		emit("dropdown:history-delete", option.value);
	}

	function historyDropDownClear() {
		emit("dropdown:history-clear");
	}

	function dropdownAction() {
		DropDownActive.value = false;
		emit("dropdown:action");
	}

	const valueFilter = ref<string>("");

	const filteredOptions = computed(() => {
		if (valueFilter.value) {
			return props.dropdownOptions?.filter((option) => {
				if (selectedDropdownValues.value.filter((item) => item === option.value).length > 0) {
					return true;
				}

				return option.label.toLowerCase().includes(valueFilter.value.toLowerCase());
			});
		} else {
			return props.dropdownOptions;
		}
	});

	function clickOutsideDropDown(event: MouseEvent) {
		// With open-on-focus, clicks inside the bar (e.g. focusing the input) must not
		// immediately close the panel the focus just opened.
		if (props.openOnFocus && rootRef.value?.contains(event.target as Node)) {
			return;
		}
		const dropDownIcon = document.querySelector(".icon-container");
		if (dropDownRef.value && dropDownIcon && !dropDownRef.value.contains(event.target as Node) && !dropDownIcon.contains(event.target as Node)) {
			DropDownActive.value = false;
		}
	}

	function toggleDropdownMenu() {
		if (!props.showDropdown || props.disabled) return;
		if (DropDownActive.value && dropdownView.value !== "full") {
			// The panel is showing suggestions or recent searches; the icon switches it to the column selector.
			dropdownView.value = "full";
			return;
		}
		dropdownView.value = "full";
		DropDownActive.value = !DropDownActive.value;
	}
	const hasContent = computed(() => {
		return value.value.trim().length > 0 || (props.allowMultiple && (values.value.length > 0 || props.valuesLabel.trim().length > 0));
	});

	//

	const isValid = computed(() => {
		if (props.allowMultiple && values.value.length > 0) {
			return true;
		}
		const trimmed = value.value.trim();
		return textValidator(trimmed);
	});

	const multiValueSeparators = /[;\n]+/g;
	const explicitOrSeparator = /\s+\bOR\b\s+/gi;

	function normalizeValues(list: string[]) {
		const seen = new Set<string>();
		const next: string[] = [];
		list.forEach((item) => {
			const trimmed = item.trim();
			if (!trimmed) return;
			if (seen.has(trimmed)) return;
			seen.add(trimmed);
			next.push(trimmed);
		});
		return next;
	}

	function splitValues(input: string) {
		const separator = props.splitOrValues ? new RegExp(`${multiValueSeparators.source}|${explicitOrSeparator.source}`, "gi") : multiValueSeparators;
		return input
			.split(separator)
			.map((item) => item.trim())
			.filter((item) => item.length > 0);
	}

	function enforceValueLimit(list: string[]) {
		const normalized = normalizeValues(list);
		const limited = limitSearchEntries(normalized, props.maxValues);
		if (limited.exceeded && limited.maxEntries !== null) {
			emit("values-limit-reached", {
				attemptedCount: normalized.length,
				maxValues: limited.maxEntries,
			});
		}
		return limited.values;
	}

	function commitValues(raw: string) {
		if (!props.allowMultiple) return;
		const items = splitValues(raw);
		if (!items.length) return;
		const next = enforceValueLimit([...values.value, ...items]);
		values.value = next;
	}

	function buildSearchSubmitPayload() {
		let nextValue = value.value;
		let nextValues = props.allowMultiple ? [...values.value] : [];

		if (editingTarget.value) {
			const target = editingTarget.value;
			const rawValue = editingValueElement.value?.textContent ?? editingValue.value;
			const replacementValues = splitValues(rawValue);
			nextValues = normalizeValues(nextValues.flatMap((item) => (item === target ? replacementValues : [item])));
			editingTarget.value = null;
			editingValue.value = "";
			editingValueElement.value = null;
			focusOut();
		}

		if (props.allowMultiple) {
			const inputValues = splitValues(nextValue);
			if (inputValues.length) {
				nextValues = normalizeValues([...nextValues, ...inputValues]);
				nextValue = "";
				value.value = "";
			}
		}

		const normalized = normalizeValues(nextValues);
		const limited = limitSearchEntries(normalized, props.maxValues);
		if (limited.exceeded && limited.maxEntries !== null) {
			emit("values-limit-reached", {
				attemptedCount: normalized.length,
				maxValues: limited.maxEntries,
			});
		}
		nextValues = limited.values;
		if (props.allowMultiple) values.value = nextValues;

		return {
			payload: {
				value: nextValue,
				values: nextValues,
			},
			limitExceeded: limited.exceeded,
		};
	}

	function emitSearchSubmit() {
		DropDownActive.value = false;
		const result = buildSearchSubmitPayload();
		if (result.limitExceeded) return;
		emit("enterpressed", result.payload);
	}

	function handleEnter() {
		if (skipNextEnter) {
			skipNextEnter = false;
			return;
		}
		if (props.allowMultiple) {
			const raw = value.value;
			commitValues(raw);
			if (raw.trim()) {
				value.value = "";
				return;
			}
		}
		emitSearchSubmit();
	}

	function handleChange() {
		if (props.allowMultiple) {
			return;
		}
		emit("change", value.value);
	}

	function confirmSearch() {
		emitSearchSubmit();
	}

	const activeSearchModeOption = computed(() => {
		if (!props.searchModes.length) return null;
		return props.searchModes.find((mode) => mode.value === props.activeSearchMode) ?? props.searchModes[0];
	});

	const isDefaultSearchMode = computed(() => {
		if (!props.searchModes.length) return true;
		return activeSearchModeOption.value?.value === props.searchModes[0].value;
	});

	function toggleModeMenu() {
		if (props.disabled) return;
		modeMenuOpen.value = !modeMenuOpen.value;
	}

	function onIconTriggerClick() {
		if (props.searchModes.length) {
			toggleModeMenu();
			return;
		}
		toggleDropdownMenu();
	}

	function selectSearchMode(mode: { value: string }) {
		modeMenuOpen.value = false;
		if (mode.value !== props.activeSearchMode) {
			emit("mode:selected", mode.value);
		}
	}

	function clickOutsideModeMenu(event: MouseEvent) {
		if (modeMenuRef.value && !modeMenuRef.value.contains(event.target as Node)) {
			modeMenuOpen.value = false;
		}
	}

	function stopEvent(event: MouseEvent) {
		event.preventDefault();
	}

	function focusEditingValue() {
		nextTick(() => {
			const element = editingValueElement.value;
			if (!element) return;
			element.focus();
			const range = document.createRange();
			range.selectNodeContents(element);
			range.collapse(false);
			const selection = window.getSelection();
			selection?.removeAllRanges();
			selection?.addRange(range);
		});
	}

	function setEditingValueElement(element: unknown) {
		editingValueElement.value = element instanceof HTMLElement ? element : null;
	}

	function startEditingValue(target: string) {
		if (props.disabled) return;
		editingTarget.value = target;
		editingValue.value = target;
		focusIn();
		focusEditingValue();
	}

	function updateEditingValue(event: Event) {
		editingValue.value = (event.currentTarget as HTMLElement | null)?.textContent ?? "";
	}

	function handleEditingValueKeydown(event: KeyboardEvent) {
		if (event.key === "Enter") {
			event.preventDefault();
			event.stopPropagation();
			confirmSearch();
			return;
		}

		if (event.key === "Escape") {
			event.preventDefault();
			event.stopPropagation();
			cancelEditingValue();
		}
	}

	function commitEditingValue() {
		const target = editingTarget.value;
		if (!target) return false;

		const rawValue = editingValueElement.value?.textContent ?? editingValue.value;
		const replacementValues = splitValues(rawValue);
		values.value = enforceValueLimit(values.value.flatMap((item) => (item === target ? replacementValues : [item])));
		editingTarget.value = null;
		editingValue.value = "";
		editingValueElement.value = null;
		focusOut();
		return true;
	}

	function cancelEditingValue() {
		editingTarget.value = null;
		editingValue.value = "";
		editingValueElement.value = null;
		focusOut();
	}

	function removeValue(target: string) {
		if (editingTarget.value === target) {
			cancelEditingValue();
		}
		const next = values.value.filter((item) => item !== target);
		values.value = next;
	}

	function clearValues() {
		values.value = [];
		emit("values-label:cleared");
	}

	watch(
		() => props.historyOptions.length,
		(length) => {
			// Keep configured history visible even when the selected window is empty.
			if (DropDownActive.value && dropdownView.value === "history" && length === 0 && !props.historyLoading && !props.suggestionsEnabled && !props.historyDropdownLabel) {
				DropDownActive.value = false;
			}
		}
	);

	watch(
		() => props.value,
		() => {
			highlightedSuggestionIndex.value = -1;
			if (!hasFocus.value || !props.suggestionsEnabled) return;
			if (!DropDownActive.value) {
				if (hasSuggestionQuery.value) openTypingPanel();
				return;
			}
			if (dropdownView.value !== "full") {
				dropdownView.value = autoPanelView();
			}
		}
	);

	watch(DropDownActive, (active) => {
		if (!active) highlightedSuggestionIndex.value = -1;
	});

	watch(
		() => props.suggestionOptions,
		() => {
			highlightedSuggestionIndex.value = -1;
		}
	);

	onMounted(() => {
		document.addEventListener("click", clickOutsideDropDown);
		document.addEventListener("click", clickOutsideModeMenu);
	});

	onBeforeUnmount(() => {
		document.removeEventListener("click", clickOutsideDropDown);
		document.removeEventListener("click", clickOutsideModeMenu);
	});
</script>

<template>
	<div
		ref="rootRef"
		class="search-input"
		@keydown="handleKeydown"
		:class="[
			{ 'has-focus': hasFocus },
			{ 'has-content': hasContent },
			{ valid: isValid },
			{ disabled: props.disabled },
			{ 'no-dropdown': props.showDropdown === false && !props.searchModes.length },
			{ 'has-dropdown': props.showDropdown !== false || props.searchModes.length > 0 },
			{ 'has-submit-button': props.showSubmitButton },
		]"
	>
		<div class="icon-container" :class="[{ 'no-dropdown': props.showDropdown === false && !props.searchModes.length }]">
			<button type="button" class="icon-button" :disabled="(!props.showDropdown && !props.searchModes.length) || props.disabled" :title="activeSearchModeOption?.description || undefined" aria-label="Toggle search dropdown" @click.stop="onIconTriggerClick">
				<svg width="19" height="20" viewBox="0 0 19 20" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path
						fill-rule="evenodd"
						clip-rule="evenodd"
						d="M12.0492 14.1349C10.7874 15.099 9.21056 15.6716 7.5 15.6716C3.35786 15.6716 0 12.3137 0 8.17161C0 4.02947 3.35786 0.671608 7.5 0.671608C11.6421 0.671608 15 4.02947 15 8.17161C15 9.88212 14.4274 11.4589 13.4634 12.7207L18.364 17.6213C18.7545 18.0118 18.7545 18.645 18.364 19.0355C17.9735 19.426 17.3403 19.426 16.9498 19.0355L12.0492 14.1349ZM13 8.17161C13 11.2092 10.5376 13.6716 7.5 13.6716C4.46243 13.6716 2 11.2092 2 8.17161C2 5.13404 4.46243 2.67161 7.5 2.67161C10.5376 2.67161 13 5.13404 13 8.17161Z"
						fill="black"
					/>
				</svg>
			</button>
			<button v-if="props.showDropdown || props.searchModes.length > 0" type="button" class="icon-button" :disabled="props.disabled" aria-label="Toggle search dropdown" @click.stop="onIconTriggerClick">
				<svg class="caret-icon" :class="{ open: DropDownActive || modeMenuOpen }" width="16" height="16" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 16 16">
					<path d="M2.67 5.33L8 10.67l5.33-5.34H2.67Z" fill="black" />
				</svg>
			</button>
		</div>
		<div class="input-surface" @dblclick="confirmSearch">
			<div class="input-content">
				<span v-if="activeSearchModeOption" class="dropdown-selection-chip mode-chip" :class="[{ 'active-mode': !isDefaultSearchMode }]" :title="activeSearchModeOption.label">
					{{ activeSearchModeOption.label }}
				</span>
				<span v-if="props.singleSelectDropdown && selectedDropdownLabel" class="dropdown-selection-chip" :title="selectedDropdownLabel">
					{{ selectedDropdownLabel }}
				</span>
				<div v-if="props.allowMultiple && props.valuesLabel" class="value-chips">
					<div class="value-chip">
						<span>{{ props.valuesLabel }}</span>
						<button type="button" class="chip-remove" @click="clearValues" aria-label="Remove values">
							<span aria-hidden="true">×</span>
						</button>
					</div>
				</div>
				<div v-else-if="props.allowMultiple && values.length" class="value-chips">
					<div v-for="item in values" :key="item" class="value-chip">
						<span
							v-if="editingTarget === item"
							:ref="setEditingValueElement"
							class="chip-label chip-edit-text"
							contenteditable="true"
							role="textbox"
							:aria-label="`Edit ${item}`"
							@input="updateEditingValue"
							@keydown="handleEditingValueKeydown"
							@blur="() => commitEditingValue()"
							@click.stop
							@dblclick.stop
						>{{ item }}</span>
						<button v-else type="button" class="chip-edit" @click="startEditingValue(item)" :aria-label="`Edit ${item}`" :title="`Edit ${item}`">
							<span class="chip-label">{{ item }}</span>
						</button>
						<button type="button" class="chip-remove" @mousedown.prevent @click="removeValue(item)" aria-label="Remove value">
							<span aria-hidden="true">×</span>
						</button>
					</div>
				</div>
				<input-text
					type="text"
					@enterpressed="handleEnter"
					:placeholder="placeholder"
					v-model:value="value"
					@change="handleChange"
					@focusin="focusIn"
					@focusout="focusOut"
					:disabled="props.disabled"
				/>
			</div>
			<button
				v-if="props.showSubmitButton"
				type="button"
				class="submit-button"
				:aria-label="props.submitButtonLabel"
				:title="props.submitButtonLabel"
				:disabled="props.disabled"
				@mousedown.stop.prevent="confirmSearch"
				@click.stop.prevent="stopEvent"
				@keydown.enter.stop.prevent="confirmSearch"
				@dblclick.stop="stopEvent"
			>
				<Search :size="16" :stroke-width="2.2" aria-hidden="true" />
			</button>
		</div>
		<div v-if="modeMenuOpen && props.searchModes.length" ref="modeMenuRef" class="mode-menu border-grey shadow-normal" role="menu">
			<button
				v-for="mode in props.searchModes"
				:key="mode.value"
				type="button"
				class="mode-menu-item"
				:class="[{ selected: mode.value === activeSearchModeOption?.value }]"
				role="menuitemradio"
				:aria-checked="mode.value === activeSearchModeOption?.value"
				@mousedown.stop.prevent
				@click.stop="selectSearchMode(mode)"
			>
				<span class="mode-menu-check" aria-hidden="true">
					<icon-complete size="small" />
				</span>
				<span class="mode-menu-text">
					<span class="mode-menu-label">{{ mode.label }}</span>
					<span v-if="mode.description" class="mode-menu-description">{{ mode.description }}</span>
				</span>
			</button>
		</div>
		<div class="dropdown border-grey open" v-if="DropDownActive && props.showDropdown" ref="dropDownRef">
			<button v-if="dropdownView === 'full' && props.dropdownActionLabel" type="button" class="dropdown-action" @click="dropdownAction">
				{{ props.dropdownActionLabel }}
			</button>
			<!-- <input-text type="text" v-model:value="valueFilter" placeholder="Enter search text" /> -->
			<div v-if="dropdownView === 'full'" class="holder">
				<div @click="dropDownSelect(option)" class="item" :class="[{ selected: selectedDropdownValues ? selectedDropdownValues.includes(option.value) : false }]" v-for="option in filteredOptions" :key="option.value">
					<div class="dropdown-icon">
						<icon-complete size="small" />
					</div>
					<span>{{ option.label }}</span>
				</div>
			</div>
			<div v-if="dropdownView === 'full' && props.secondaryDropdownOptions.length > 0" class="secondary-holder">
				<p v-if="props.secondaryDropdownLabel" class="secondary-label">{{ props.secondaryDropdownLabel }}</p>
				<div @click="secondaryDropDownSelect(option)" class="item secondary-item" v-for="option in props.secondaryDropdownOptions" :key="option.value">
					<div class="secondary-item-main">
						<div class="dropdown-icon saved-icon">
							<icon-complete size="small" />
						</div>
						<span>{{ option.label }}</span>
					</div>
					<div class="secondary-item-actions">
						<button
							v-if="props.secondaryDropdownDownloadLabel"
							type="button"
							class="secondary-action secondary-download"
							:aria-label="props.secondaryDropdownDownloadLabel"
							:title="props.secondaryDropdownDownloadLabel"
							@click.stop="secondaryDropDownDownload(option)"
						>
							<Download :size="17" :stroke-width="2" aria-hidden="true" />
						</button>
						<button v-if="props.secondaryDropdownDeleteLabel" type="button" class="secondary-action secondary-delete" :aria-label="props.secondaryDropdownDeleteLabel" :title="props.secondaryDropdownDeleteLabel" @click.stop="secondaryDropDownDelete(option)">
							<span aria-hidden="true">×</span>
						</button>
					</div>
				</div>
			</div>
			<div v-if="dropdownView === 'suggestions'" class="secondary-holder suggestion-holder" role="listbox">
				<p v-if="props.suggestionDropdownLabel" class="secondary-label">{{ props.suggestionDropdownLabel }}</p>
				<p v-if="props.suggestionOptions.length === 0" class="history-empty">{{ props.suggestionsLoading ? "Searching…" : props.suggestionEmptyLabel }}</p>
				<div
					v-for="(option, index) in props.suggestionOptions"
					:key="option.value"
					class="item secondary-item suggestion-item"
					:class="{ highlighted: index === highlightedSuggestionIndex }"
					:title="option.title || undefined"
					role="option"
					:aria-selected="index === highlightedSuggestionIndex"
					@mousedown.prevent
					@mouseenter="highlightedSuggestionIndex = index"
					@click.stop="suggestionDropDownSelect(option)"
				>
					<div class="secondary-item-main">
						<div class="dropdown-icon suggestion-icon">
							<Search :size="13" :stroke-width="2" aria-hidden="true" />
						</div>
						<span class="history-text">
							<span class="history-label">{{ option.label }}</span>
							<span v-if="option.description" class="history-description">{{ option.description }}</span>
						</span>
					</div>
				</div>
			</div>
			<div v-if="dropdownView === 'history' && props.historyDropdownLabel" class="secondary-holder history-holder">
				<div class="secondary-label-row">
					<p class="secondary-label">{{ props.historyDropdownLabel }}</p>
					<button v-if="props.historyClearLabel && props.historyOptions.length > 0" type="button" class="history-clear" @click.stop="historyDropDownClear">
						{{ props.historyClearLabel }}
					</button>
				</div>
				<p v-if="props.historyOptions.length === 0" class="history-empty">{{ props.historyLoading ? "Loading searches…" : props.historyEmptyLabel }}</p>
				<div
					v-for="option in props.historyOptions"
					:key="option.value"
					class="item secondary-item history-item"
					:title="option.title || undefined"
					@click="historyDropDownSelect(option)"
				>
					<div class="secondary-item-main">
						<div class="dropdown-icon history-icon">
							<History :size="14" :stroke-width="2" aria-hidden="true" />
						</div>
						<span class="history-text">
							<span class="history-label">{{ option.label }}</span>
							<span v-if="option.description" class="history-description">{{ option.description }}</span>
						</span>
					</div>
					<button v-if="props.historyDeleteLabel" type="button" class="secondary-delete" :aria-label="props.historyDeleteLabel" :title="props.historyDeleteLabel" @click.stop="historyDropDownDelete(option)">
						<span aria-hidden="true">×</span>
					</button>
				</div>
				<div v-if="props.historyActionLabel" class="history-footer">
					<button type="button" class="history-action" :disabled="props.historyLoading" @click.stop="emit('dropdown:history-action')">
						{{ props.historyLoading ? "Loading searches…" : props.historyActionLabel }}
					</button>
				</div>
			</div>
			<!-- 	<div class="dropdown-content" :class="[{ 'selected': item.selected }]" v-for="(item, index) in dropdownOptions" :key="index" :id="item.id">
				<input type="radio" :value="item.value" name="searchOption" @click="dropDownSelect(item)" />
				<label :for="item.id">{{ item.label }}</label>
				 
			</div> -->
			<!-- <template v-else>
				<template v-for="(item, index) in suggestionsList" :key="index">
					<div class="dropdown-content" @click="suggesionClicked(item)">
						<span>
							{{ item }}
						</span>
					</div>
				</template>
<div class="dropdown-content no-data" v-if="!suggestionsList.length">
	No Data Found
</div>
</template> -->
		</div>
	</div>
</template>

<style scoped lang="scss">
	.search-input {
		position: relative;
		display: flex;
		transition: all 0.1s ease-out, height 0s;
		width: 100%;
		@extend .gpu;
		pointer-events: all;
		flex-direction: column !important;
		text-align: left;
		width: 100%;
		margin-bottom: 25px;
		z-index: 9;

		.icon-container {
			position: absolute;
			display: flex;
			align-items: center;
			top: 50%;
			left: 20px;
			transform: translateY(-50%);
			cursor: pointer;
			z-index: 2;

			.icon-button {
				display: inline-flex;
				align-items: center;
				justify-content: center;
				padding: 0;
				margin: 0;
				border: none;
				background: transparent;
				cursor: pointer;
				line-height: 0;
			}

			.caret-icon {
				transition: transform 0.15s ease;

				&.open {
					transform: rotate(180deg);
				}
			}

			&.no-dropdown {
				cursor: default;

				.icon-button {
					cursor: default;
				}
			}
		}

		.icon {
			position: absolute;
			top: 50%;
			left: 20px;
			transform: translateY(-50%);
			pointer-events: none;
			z-index: 1;
		}

		.input-surface {
			position: relative;
			display: flex;
			align-items: center;
			gap: 8px;
			width: 100%;
			border: 1px solid $grey;
			border-radius: 22px;
			background: white;
			padding: 0 15px 0 60px;
			box-sizing: border-box;
			transition: inherit;
			height: 40px;
		}

		&.has-submit-button {
			.input-surface {
				padding-right: 52px;
			}
		}

		.input-content {
			display: flex;
			align-items: center;
			flex-wrap: nowrap;
			flex: 1;
			min-width: 0;
			gap: 8px;
			height: 40px;
			overflow-x: auto;
			overflow-y: hidden;
			scrollbar-width: none;
			&::-webkit-scrollbar {
				height: 0;
				width: 0;
			}
		}

		.submit-button {
			position: absolute;
			top: 50%;
			right: 6px;
			transform: translateY(-50%);
			display: inline-flex;
			align-items: center;
			justify-content: center;
			width: 30px;
			height: 30px;
			padding: 0;
			border: none;
			border-radius: 50%;
			background: $red-semi;
			color: $black;
			cursor: pointer;
			transition: background 0.1s ease-in-out, color 0.1s ease-in-out;

			&:hover:not(:disabled) {
				background: $black;
				color: white;
			}

			&:disabled {
				cursor: not-allowed;
				opacity: 0.5;
			}
		}

		.mode-menu {
			position: absolute;
			top: calc(100% + 8px);
			left: 0;
			z-index: 10000;
			min-width: 260px;
			max-width: 340px;
			padding: 6px;
			border-radius: 12px;
			background: white;
			display: flex;
			flex-direction: column;
			gap: 2px;

			.mode-menu-item {
				display: flex;
				align-items: flex-start;
				gap: 10px;
				width: 100%;
				padding: 9px 10px;
				border: none;
				border-radius: 8px;
				background: transparent;
				text-align: left;
				cursor: pointer;
				transition: background 0.1s ease-in-out;

				&:hover {
					background: $grey-light;
				}

				.mode-menu-check {
					flex-shrink: 0;
					width: 22px;
					height: 22px;
					margin-top: 1px;
					display: flex;
					align-items: center;
					justify-content: center;
					border-radius: 50%;
					background: $grey-semi;
					transition: background 0.1s ease-in-out;

					:deep(svg) {
						opacity: 0;
						transition: opacity 0.1s ease-in-out;
						rect {
							fill: white;
						}
					}
				}

				&.selected .mode-menu-check {
					background: $red;

					:deep(svg) {
						opacity: 1;
					}
				}

				.mode-menu-text {
					display: flex;
					flex-direction: column;
					gap: 2px;
					min-width: 0;
				}

				.mode-menu-label {
					color: $black;
					font-size: 14px;
					@include FontWeightMedium(false);
					line-height: 1.2;
				}

				.mode-menu-description {
					color: rgba(0, 0, 0, 0.55);
					font-size: 12px;
					line-height: 1.3;
				}
			}
		}

		.dropdown-selection-chip {
			display: inline-flex;
			align-items: center;
			gap: 6px;
			padding: 4px 10px;
			border-radius: 999px;
			background: $grey_semi;
			color: $black;
			font-size: 12px;
			font-weight: 600;
			max-width: 200px;
			white-space: nowrap;
			overflow: hidden;
			text-overflow: ellipsis;
			flex-shrink: 0;

			&.mode-chip.active-mode {
				background: $red-semi;
				color: $red;
			}
		}

		input[type="text"] {
			font-size: 18px;
			background: transparent;
			border-radius: 0;
			outline: none;
			cursor: text !important;
			@include h4(false);
			line-height: 40px;
			height: 40px;
			flex: 1 0 140px;
			min-width: 160px;
			box-sizing: border-box;
			transition: inherit;
			pointer-events: all;
			padding: 0;
			border: none;
		}

		.dropdown-container {
			position: absolute;
			width: 100%;
			top: 100%;
			background: white;
			border: 1px solid $grey;
			padding: 10px;
			flex-direction: column;

			.selected {
				font-weight: bold;
				background-color: $red-semi;
			}

			.dropdown-content {
				display: flex;
				flex-direction: column;
				position: relative;
				margin: 5px 0;

				span {
					padding: 5px;
					margin: 5px 0;
					cursor: pointer;

					&:hover {
						background-color: $red-semi;
					}
				}

				input[type="radio"] {
					position: absolute;
					opacity: 0;
					width: 100%;
					height: 100%;
					cursor: pointer;

					&:hover {
						& ~ label {
							background-color: $red-semi;
						}
					}
				}

				label {
					display: block;
					padding: 10px;
					cursor: pointer;
					width: 100%;
				}
			}

			.no-data {
				display: block;
				color: grey;
				text-align: center;
			}
		}

		&.has-content,
		&.has-focus {
			.input-surface {
				border-color: $black;
			}
		}

		&.disabled {
			opacity: 0.5;
			pointer-events: none;

			input[type="text"] {
				background: $grey-light;
				cursor: not-allowed !important;
			}

			.input-surface {
				background: $grey-light;
			}
		}

		&.no-dropdown {
			.input-surface {
				padding-left: 45px;
			}
		}

		.value-chips {
			display: inline-flex;
			flex-wrap: nowrap;
			gap: 8px;
			margin: 0;
			flex-shrink: 0;
		}

		.value-chip {
			display: inline-flex;
			align-items: center;
			gap: 6px;
			padding: 4px 10px;
			border-radius: 999px;
			background: $grey_semi;
			color: $black;
			font-size: 12px;
			font-weight: 600;
			max-width: 100%;
			height: 24px;

			span {
				white-space: nowrap;
				overflow: hidden;
				text-overflow: ellipsis;
				max-width: 220px;
			}

			.chip-edit {
				display: inline-flex;
				align-items: center;
				min-width: 0;
				padding: 0;
				border: none;
				background: transparent;
				color: inherit;
				cursor: text;
				font: inherit;
				line-height: 18px;
			}

			.chip-edit-text {
				display: inline-block;
				line-height: 18px;
				min-height: 18px;
				outline: none;
				caret-color: $black;
				cursor: text;
			}

			.chip-remove {
				border: none;
				background: transparent;
				color: rgba(0, 0, 0, 0.5);
				cursor: pointer;
				font-size: 16px;
				line-height: 1;
				padding: 0;
				display: inline-flex;
				align-items: center;
				justify-content: center;

				&:hover {
					color: rgba(0, 0, 0, 0.8);
				}
			}
		}

	}
	.dropdown {
		top: 100%;
		flex-direction: column;
		border-radius: 12px;
		overflow: hidden;
		width: 100%;
		transform: scale(0.98) translateY(-10px);
		transform-origin: top center;
		opacity: 0;
		//position: fixed;
		position: absolute;
		background: white;
		z-index: 10000;
		pointer-events: none;
		box-shadow: 0px 20px 40px rgba(0, 0, 0, 0.1);
		transition: all 0.1s ease-in-out;

		input {
			line-height: 44px;
			padding-left: 20px;
			padding-right: 20px;
			border-bottom: 1px solid $grey;
		}

		.dropdown-action {
			display: flex;
			align-items: center;
			width: 100%;
			height: 42px;
			padding: 0 20px;
			border: none;
			border-bottom: 1px solid $grey;
			background: $red-semi;
			color: $black;
			cursor: pointer;
			@include GoogleSans(false);
			@include FontWeightMedium(false);

			&:hover {
				background: $grey-light;
			}
		}

		.holder {
			display: flex;
			flex-direction: column;
			max-height: 300px;
			overflow-y: auto;

			&::-webkit-scrollbar {
				width: 8px;
				background-color: rgba(0, 0, 0, 0);
			}
			&::-webkit-scrollbar-thumb {
				background-color: rgba(0, 0, 0, 0.25);
				min-height: 30%;
				border-radius: 8px;
			}
		}

		.secondary-holder {
			display: flex;
			flex-direction: column;
			border-top: 1px solid $grey;
			max-height: 220px;
			overflow-y: auto;

			&:first-child {
				border-top: none;
			}
		}

		.secondary-label {
			margin: 0;
			padding: 10px 20px 6px;
			font-size: 11px;
			text-transform: uppercase;
			letter-spacing: 0.06em;
			color: #7d7d7d;
			@include FontWeightMedium(false);
		}

		.secondary-label-row {
			display: flex;
			align-items: center;
			justify-content: space-between;
			padding-right: 12px;

			.secondary-label {
				padding-right: 0;
			}

			.history-clear {
				margin-left: auto;
				border: none;
				background: transparent;
				color: #7d7d7d;
				cursor: pointer;
				font-size: 11px;
				text-transform: uppercase;
				letter-spacing: 0.06em;
				padding: 10px 8px 6px;
				@include FontWeightMedium(false);

				&:hover {
					color: $red;
				}
			}
		}

		.history-footer {
			flex-shrink: 0;
			padding: 10px 20px;
		}

		.history-action {
			width: 100%;
			border: none;
			border-radius: 6px;
			padding: 8px 12px;
			background: #f5f5f5;
			color: inherit;
			font: inherit;
			font-size: 12px;
			cursor: pointer;

			&:hover:not(:disabled) {
				background: #ebebeb;
			}

			&:focus-visible {
				outline: 2px solid currentColor;
				outline-offset: 2px;
			}

			&:disabled {
				opacity: 0.6;
				cursor: wait;
			}
		}

		.history-empty {
			margin: 0;
			padding: 4px 20px 12px;
			font-size: 12px;
			color: #7d7d7d;
		}

		&.open {
			display: flex;
			opacity: 1;
			transform: scale(1) translateY(0px);
			pointer-events: all;
		}
		.item {
			display: flex;
			align-items: center;
			flex-shrink: 0;
			padding: 0 20px;
			height: 44px;
			border-bottom: 1px solid $grey;
			cursor: pointer;
			gap: 10px;
			color: black;
			@include GoogleSans(false);
			@include FontWeightRegular(false);
			user-select: none;
			transition: all 0.1s ease-in-out;
			&:last-child {
				border-bottom: none;
			}

			&:hover {
				background: $grey-light;
			}

			span {
				white-space: nowrap;
				overflow: hidden;
				text-overflow: ellipsis;
			}

			.dropdown-icon {
				flex-shrink: 0;
				width: 26px;
				height: 26px;
				display: flex;
				background: $grey-semi;
				align-items: center;
				justify-content: center;
				border-radius: 18px;
				overflow: hidden;
				transition: all 0.2s ease-in-out;

				:deep(svg) {
					opacity: 0;
					transition: all 0.1s ease-in-out;
					rect {
						fill: white;
					}
				}
			}

			&.selected {
				.dropdown-icon {
					background: black;

					:deep(svg) {
						opacity: 1;
					}
				}
			}

			&.secondary-item {
				height: 42px;
				justify-content: space-between;
				padding-right: 12px;

				.saved-icon {
					background: $red-semi;
				}
			}

			&.suggestion-item {
				height: auto;
				min-height: 44px;
				padding-top: 6px;
				padding-bottom: 6px;

				.suggestion-icon {
					background: $grey-light;
					color: rgba(0, 0, 0, 0.6);

					:deep(svg) {
						opacity: 1;
					}
				}

				&.highlighted {
					background: $grey-light;
				}

				.history-text {
					display: flex;
					flex-direction: column;
					gap: 2px;
					min-width: 0;
				}

				.history-label {
					white-space: nowrap;
					overflow: hidden;
					text-overflow: ellipsis;
					font-size: 14px;
					line-height: 1.3;
				}

				.history-description {
					white-space: nowrap;
					overflow: hidden;
					text-overflow: ellipsis;
					font-size: 12px;
					line-height: 1.3;
					color: rgba(0, 0, 0, 0.55);
				}
			}

			&.history-item {
				height: auto;
				min-height: 48px;
				padding-top: 6px;
				padding-bottom: 6px;

				.history-icon {
					background: $grey-light;
					color: rgba(0, 0, 0, 0.6);

					:deep(svg) {
						opacity: 1;
					}
				}

				.history-text {
					display: flex;
					flex-direction: column;
					gap: 2px;
					min-width: 0;
				}

				.history-label {
					white-space: nowrap;
					overflow: hidden;
					text-overflow: ellipsis;
					font-size: 14px;
					line-height: 1.3;
				}

				.history-description {
					white-space: nowrap;
					overflow: hidden;
					text-overflow: ellipsis;
					font-size: 12px;
					line-height: 1.3;
					color: rgba(0, 0, 0, 0.55);
				}
			}

			.secondary-item-main {
				display: flex;
				align-items: center;
				min-width: 0;
				gap: 10px;
			}

			.secondary-item-actions {
				display: flex;
				align-items: center;
				gap: 2px;
				flex-shrink: 0;
			}

			.secondary-action,
			.secondary-delete {
				display: flex;
				align-items: center;
				justify-content: center;
				flex-shrink: 0;
				width: 26px;
				height: 26px;
				border: none;
				border-radius: 50%;
				background: transparent;
				color: $black;
				cursor: pointer;
				font-size: 18px;
				line-height: 1;

				&:hover {
					background: $grey-semi;
				}
			}
		}
	}
</style>
